import React, { useState } from 'react';
import { cn } from '@/lib/utils';

export type ConversationStage = 'initial' | 'strategy' | 'proposal' | 'published';

interface KnowledgeCard {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

interface KnowledgeCardsProps {
  stage: ConversationStage;
  /** Platforms from the active strategy, for contextual cards */
  strategyPlatforms?: string[];
  /** Campaign objective from the active strategy */
  strategyObjective?: string;
  className?: string;
}

// Platform strength descriptions for contextual cards
const platformStrengths: Record<string, string> = {
  meta: 'Meta reaches broad audiences with visual storytelling across Facebook and Instagram feeds, stories, and reels.',
  google_ads: 'Google Ads captures people actively searching for your product or service, with high purchase intent.',
  tiktok: 'TikTok delivers viral reach with short-form video, especially effective for younger demographics (18-34).',
  pinterest: 'Pinterest connects with users in a discovery and planning mindset, ideal for visual and aspirational products.',
  twitter: 'Twitter/X drives real-time engagement and conversation, strong for B2B, tech, and news-oriented audiences.',
};

// Objective explanations
const objectiveExplanations: Record<string, string> = {
  awareness: 'Awareness campaigns maximize how many people see your brand. Best for new businesses or product launches. You pay per impression (CPM).',
  traffic: 'Traffic campaigns drive visitors to your website or landing page. The platform optimizes for people likely to click your ad.',
  engagement: 'Engagement campaigns maximize likes, comments, shares, and saves. Great for building social proof and community.',
  leads: 'Lead generation campaigns collect contact info directly within the platform (no website needed). Ideal for service businesses.',
  app_promotion: 'App promotion campaigns drive installs and in-app actions. Platforms target users likely to download and engage.',
  sales: 'Sales/conversion campaigns target people most likely to purchase. Requires a tracking pixel on your website for best results.',
  conversions: 'Conversion campaigns target people most likely to take action on your website -- purchases, sign-ups, or form submissions.',
  CONVERSIONS: 'Conversion campaigns target people most likely to take action on your website -- purchases, sign-ups, or form submissions.',
  AWARENESS: 'Awareness campaigns maximize how many people see your brand. Best for new businesses or product launches.',
  TRAFFIC: 'Traffic campaigns drive visitors to your website. The platform optimizes for people likely to click.',
  LEAD_GENERATION: 'Lead generation campaigns collect contact info directly within the platform. Ideal for service businesses.',
  ENGAGEMENT: 'Engagement campaigns maximize likes, comments, shares, and saves. Great for building community.',
  SALES: 'Sales campaigns target people most likely to purchase. Requires a tracking pixel for best results.',
};

const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    className={cn(
      'w-4 h-4 text-ink-3 transition-transform duration-200 flex-shrink-0',
      expanded && 'rotate-180'
    )}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

// --- Card content per stage ---

function getInitialCards(): KnowledgeCard[] {
  return [
    {
      id: 'platforms-guide',
      title: 'Choosing the right platforms',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      content: (
        <ul className="space-y-2 text-xs text-ink-2">
          <li><span className="font-medium text-ink-2">Visual product?</span> Meta or TikTok for image/video-driven reach</li>
          <li><span className="font-medium text-ink-2">People search for it?</span> Google Ads to capture purchase intent</li>
          <li><span className="font-medium text-ink-2">Younger audience?</span> TikTok for 18-34 demographics</li>
          <li><span className="font-medium text-ink-2">Inspiration/planning?</span> Pinterest for discovery-phase users</li>
        </ul>
      ),
    },
    {
      id: 'objectives-overview',
      title: 'Campaign objectives explained',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      content: (
        <div className="space-y-2 text-xs text-ink-2">
          <p><span className="font-medium text-ink-2">Awareness</span> -- Maximize visibility. Best for new brands.</p>
          <p><span className="font-medium text-ink-2">Traffic</span> -- Drive website visits. Good for content and landing pages.</p>
          <p><span className="font-medium text-ink-2">Conversions/Sales</span> -- Optimize for purchases or sign-ups. Needs tracking pixel.</p>
          <p><span className="font-medium text-ink-2">Leads</span> -- Collect contacts in-platform. No website needed.</p>
        </div>
      ),
    },
    {
      id: 'budget-allocation',
      title: 'How budgets are split',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      content: (
        <div className="space-y-2 text-xs text-ink-2">
          <p>Budget is allocated based on where your audience is most active and each platform's strengths.</p>
          <p><span className="font-medium text-ink-2">Meta</span> excels at broad awareness. <span className="font-medium text-ink-2">Google</span> captures high purchase-intent searches. <span className="font-medium text-ink-2">TikTok</span> offers viral potential at lower CPMs.</p>
        </div>
      ),
    },
  ];
}

function getStrategyCards(platforms?: string[], objective?: string): KnowledgeCard[] {
  const cards: KnowledgeCard[] = [];

  // Contextual: Why these platforms
  if (platforms && platforms.length > 0) {
    const explanations = platforms
      .map(p => platformStrengths[p])
      .filter(Boolean);
    if (explanations.length > 0) {
      cards.push({
        id: 'why-platforms',
        title: 'Why these platforms?',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        ),
        content: (
          <div className="space-y-1.5 text-xs text-ink-2">
            {explanations.map((exp, i) => (
              <p key={i}>{exp}</p>
            ))}
          </div>
        ),
      });
    }
  }

  // Contextual: Objective explanation
  if (objective) {
    const explanation = objectiveExplanations[objective] || objectiveExplanations[objective.toUpperCase()];
    if (explanation) {
      cards.push({
        id: 'objective-explained',
        title: `What does "${objective.replace(/_/g, ' ').toLowerCase()}" mean?`,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        content: (
          <p className="text-xs text-ink-2">{explanation}</p>
        ),
      });
    }
  }

  // Static: Campaign types
  cards.push({
    id: 'campaign-types',
    title: 'Understanding campaign types',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    content: (
      <div className="space-y-2 text-xs text-ink-2">
        <p><span className="font-medium text-ink-2">Google Search</span> -- Text ads shown when people search specific keywords.</p>
        <p><span className="font-medium text-ink-2">Performance Max</span> -- Google's AI shows ads across Search, Display, YouTube, and Gmail automatically.</p>
        <p><span className="font-medium text-ink-2">Meta Feed Ads</span> -- Image or video ads in Facebook/Instagram feeds.</p>
        <p><span className="font-medium text-ink-2">TikTok In-Feed</span> -- Native vertical video ads that blend with organic content.</p>
      </div>
    ),
  });

  return cards;
}

function getProposalCards(): KnowledgeCard[] {
  return [
    {
      id: 'ad-formats',
      title: 'Ad format guide',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      content: (
        <div className="space-y-2 text-xs text-ink-2">
          <p><span className="font-medium text-ink-2">Single image</span> -- Simple, fast to produce. Works well for clear product shots.</p>
          <p><span className="font-medium text-ink-2">Carousel</span> -- Multiple images users swipe through. Great for showcasing features or product range.</p>
          <p><span className="font-medium text-ink-2">Video</span> -- Highest engagement rates. Best for storytelling and demonstrations.</p>
          <p><span className="font-medium text-ink-2">Stories/Reels</span> -- Full-screen vertical format. Feels native and immersive.</p>
        </div>
      ),
    },
    {
      id: 'targeting-tips',
      title: 'Targeting tips',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      content: (
        <div className="space-y-2 text-xs text-ink-2">
          <p><span className="font-medium text-ink-2">Interest targeting</span> -- Reaches users based on their behaviors and interests. Broad but effective for awareness.</p>
          <p><span className="font-medium text-ink-2">Keyword targeting</span> -- Google Search only. Targets specific search terms for high-intent traffic.</p>
          <p>Start broader and narrow down based on performance data after 3-7 days.</p>
        </div>
      ),
    },
    {
      id: 'ab-testing',
      title: 'A/B testing: what to expect',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      content: (
        <div className="space-y-2 text-xs text-ink-2">
          <p>When multiple ad variations are published, platforms automatically test them and show the best performers more often.</p>
          <p>Results take <span className="font-medium text-ink-2">3-7 days</span> to stabilize. Avoid making changes too early.</p>
        </div>
      ),
    },
  ];
}

function getPublishedCards(): KnowledgeCard[] {
  return [
    {
      id: 'key-metrics',
      title: 'Key metrics to watch',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      content: (
        <div className="space-y-2 text-xs text-ink-2">
          <p><span className="font-medium text-ink-2">CTR (Click-Through Rate)</span> -- % of people who clicked your ad. Higher = more compelling ad.</p>
          <p><span className="font-medium text-ink-2">CPC (Cost Per Click)</span> -- How much each click costs. Lower is better.</p>
          <p><span className="font-medium text-ink-2">ROAS (Return on Ad Spend)</span> -- Revenue generated per dollar spent. Above 1.0 = profitable.</p>
        </div>
      ),
    },
    {
      id: 'when-to-optimize',
      title: 'When to optimize',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      content: (
        <div className="space-y-2 text-xs text-ink-2">
          <p>Give your campaign <span className="font-medium text-ink-2">3-7 days</span> before making changes. Platforms need time to optimize delivery.</p>
          <p>Look for <span className="font-medium text-ink-2">trends over 3+ days</span>, not single-day spikes or dips.</p>
          <p>Common actions: pause underperformers, increase budget on winners, test new creatives.</p>
        </div>
      ),
    },
  ];
}

function getCardsForStage(
  stage: ConversationStage,
  platforms?: string[],
  objective?: string,
): KnowledgeCard[] {
  switch (stage) {
    case 'initial':
      return getInitialCards();
    case 'strategy':
      return getStrategyCards(platforms, objective);
    case 'proposal':
      return getProposalCards();
    case 'published':
      return getPublishedCards();
    default:
      return getInitialCards();
  }
}

const stageLabels: Record<ConversationStage, string> = {
  initial: 'Getting Started',
  strategy: 'Strategy Review',
  proposal: 'Campaign Proposal',
  published: 'Post-Launch',
};

export const KnowledgeCards: React.FC<KnowledgeCardsProps> = ({
  stage,
  strategyPlatforms,
  strategyObjective,
  className,
}) => {
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const cards = getCardsForStage(stage, strategyPlatforms, strategyObjective);

  const toggleCard = (cardId: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  };

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-center gap-1.5 px-1 mb-2">
        <svg className="w-3.5 h-3.5 text-work" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-[11px] font-medium uppercase tracking-wider text-ink-3">
          {stageLabels[stage]}
        </span>
      </div>

      {cards.map(card => {
        const isExpanded = expandedCards.has(card.id);
        return (
          <div key={card.id} className="rounded-lg border border-line overflow-hidden">
            <button
              onClick={() => toggleCard(card.id)}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-ground transition-colors"
            >
              <span className="text-work flex-shrink-0">{card.icon}</span>
              <span className="text-xs font-medium text-ink-2 flex-1 min-w-0">
                {card.title}
              </span>
              <ChevronIcon expanded={isExpanded} />
            </button>
            {isExpanded && (
              <div className="px-3 pb-3 pt-0.5 border-t border-line">
                {card.content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default KnowledgeCards;
